import { proxy } from 'valtio';
import { Edge, Node, ReactFlowInstance } from 'reactflow';
import { Editor, JSONContent } from '@tiptap/react';
import { RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';

// A point-in-time copy of the graph on the undo/redo stacks. Defined here, not in
// useUndoRedo, to avoid an import cycle (useUndoRedo already imports this module).
export type FlowHistoryItem = {
  nodes: Node[];
  edges: Edge[];
};

class FlowEditorState {
  isConnectable: boolean = false;
  selectedNodeId: string | null = null;
  resource: RouterOutputs['resources']['byId'] | null = null;
  defaultPersonaId: string | null = 'defaultPersonaId'; // TODO load up from db the mostly recently used persona id
  simulationActive = false;
  connectedOptionIds: Set<string> = new Set<string>();
  endNodeIds: Set<string> = new Set<string>();
  nodeIdToEditorMap: Map<string, Editor | null> = new Map();

  flowEditor: ReactFlowInstance<any, any> | null = null;

  // Version counters for unsaved edits: clientVersion bumps on every content change,
  // serverVersion catches up on save, the flow is dirty while they differ. Held here,
  // not in React state, so a keystroke marking dirty doesn't re-render the canvas.
  clientVersion = 0;
  serverVersion = 0;

  // Bumped on every undo/redo restore. The selected node's editable subtree keys its
  // remount off this (via subscribeKey, never a snapshot, so keystrokes don't
  // re-render it) so it rebuilds from the restored node.data rather than keeping its
  // buffered local state and uncontrolled inputs.
  restoreNonce = 0;

  // The recorder interface, registered by the FlowEditor recorder so useUndoRedo
  // (in a different component) can reach it without prop-drilling.

  // Flush the selected node's debounced editor content into node.data now, so a
  // capture straight after typing isn't missing the last keystrokes.
  flushActiveEditor: () => void = () => {};

  // Cancel the selected node's pending 300ms node.data debounce. undo/redo call this
  // so a keystroke buffered before the press can't fire its late write onto the
  // restored graph and desync the stack. Registered by the selected node.
  cancelActiveEditorDebounce: () => void = () => {};

  // The selected node's live editor content, read synchronously. The recorder
  // overlays this so the capture carries keystrokes the debounce hasn't committed.
  getActiveEditorContent: () => { id: string; tiptap: JSONContent | undefined } | null = () => null;

  // getCurrentGraph: the current graph (render-synced mirror + live keystrokes).
  // applyRestoredGraph: apply a restored graph AND re-baseline the recorder in one
  // synchronous turn, so the recorder doesn't mistake the restore for a fresh edit.
  getCurrentGraph: () => FlowHistoryItem = () => ({ nodes: [], edges: [] });
  applyRestoredGraph: (graph: FlowHistoryItem) => void = () => {};

  // What undo/redo stash: the recorder's baseline ref, synchronously current on every
  // edit and restore, so burst undo/redo stashes the right graph, not a lagged one.
  getRecordedGraph: () => FlowHistoryItem = () => ({ nodes: [], edges: [] });

  // Signal a keystroke so the recorder coalesces a per-node typing run and breaks it
  // when editing moves to another node.
  noteTextEdit: (nodeId: string) => void = () => {};

  // Close the open typing run synchronously. The Enter / menu-Message handler calls
  // this on appending an empty placeholder, finalising the completed message as its
  // own undo entry at an unambiguous action rather than an inferred diff. Registered
  // by the recorder.
  endTextRunNow: () => void = () => {};

  // Whether the flow has unsaved edits. A derived boolean so consumers (e.g. the
  // publish button) subscribe to the dirty/clean transition, not to clientVersion.
  get isDirty() {
    return this.clientVersion !== this.serverVersion;
  }

  // Mark the flow dirty and queue an autosave. Call only on a real content change,
  // never on a bare selection or click. The optional `source` is a dev-only label
  // the undo inspector records so a stray bump is attributable.
  markDirty(source?: string) {
    this.clientVersion += 1;
    this.lastDirtySource = source ?? 'unknown';
  }

  // The most recent markDirty() label, read by the recorder when it logs. Dev only.
  lastDirtySource = 'init';

  // Pass the clientVersion current when the payload was built, not the live one: the
  // save awaits the server, so an edit landing mid-save must stay dirty.
  markSaved(version: number) {
    this.serverVersion = version;
  }

  markRestored() {
    this.restoreNonce += 1;
  }

  setIsConnectable(isConnectable: boolean) {
    this.isConnectable = isConnectable;
  }

  setSelectedNodeId(nodeId: string) {
    this.selectedNodeId = nodeId;
  }

  unselectNode() {
    this.selectedNodeId = null;
  }

  setConnectedOptionIds(ids: string[]) {
    this.connectedOptionIds = new Set(ids);
  }

  setEndNodeIds(ids: string[]) {
    this.endNodeIds = new Set(ids);
  }

  setNodeIdToEditorMap(id: string, editor: Editor) {
    this.nodeIdToEditorMap.set(id, editor);
  }

  deleteNodeIdToEditorMap(id: string) {
    this.nodeIdToEditorMap.delete(id);
  }
}

export const flowEditorState = proxy(new FlowEditorState());
